import { useState } from 'react';
import { Box } from '@coolblue-development/becky';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { EmailEditorEventPlugin } from './EmailEditorEventPlugin';
import { MATCHERS, validateUrl } from '../../../../../../utils/regex';
import ItalicIcon from '../../../../../../assets/italic.svg';
import BoldIcon from '../../../../../../assets/bold.svg';
import BulletListIcon from '../../../../../../assets/bulletList.svg';
import NumberedListIcon from '../../../../../../assets/numberedList.svg';
import MarkdownButton from '../../../Chat/InputForm/MarkdownButton';
import MarkdownListButton from '../../../Chat/InputForm/MarkdownListButton';
import LinkButton from '../../../Chat/InputForm/LinkButton';
import QuickRepliesContainer from '../../../Chat/InputForm/QuickRepliesContainer';
import { Divider } from '@coolblue-development/freddy';
import DiscardButton from './DiscardButton';
import EmailQuickReplies from './EmailQuickReplies';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { ImageNode } from './ImageNode';
import ImagePastePlugin from './ImagePastePlugin';
import ImageUploadButton from './ImageUploadButton';
import AttachmentUploadButton from './AttachmentUploadButton';
import { Attachment, MAX_ATTACHMENT_COUNT } from '../../../../../../models/Attachment';
import { SendButton } from '../SendButton/SendButton';
import { useSendEmail } from './useSendEmail';
import EmailMessageTemplates from './EmailMessageTemplates';
import { OutboundEmailFields } from '../../Outbound/EmailOutboundHeader';
import TemplatePreview from './TemplatePreview';

const theme = {
  text: {
    bold: 'bold',
    italic: 'italic'
  }
};

const initialConfig = {
  namespace: 'EmailEditor',
  theme,
  nodes: [ListNode, ListItemNode, LinkNode, AutoLinkNode, ImageNode],
  onError: () => {
    console.error();
  }
};

interface EmailEditorProps {
  onDiscard: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  onBodySizeExceeded?: (exceeded: boolean) => void;
  attachments: Attachment[];
  onFilesSelected: (files: FileList) => void | Promise<void>;
  isSendDisabled?: boolean;
  isUploadingAttachments?: boolean;
  isAttachmentUploadDisabled?: boolean;
  outboundFields?: OutboundEmailFields;
}

interface SendEmailPluginProps {
  onDiscard: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  isSendDisabled?: boolean;
  outboundFields?: OutboundEmailFields;
  attachments: Attachment[];
}

function SendEmailPlugin({
  onDiscard,
  onSuccess,
  onError,
  isSendDisabled,
  outboundFields,
  attachments
}: SendEmailPluginProps): JSX.Element {
  const { handleSend, isLoading } = useSendEmail({ onDiscard, onSuccess, onError, outboundFields, attachments });

  return <SendButton onClick={handleSend} isLoading={isLoading} disabled={isSendDisabled} />;
}

const EmailEditor = ({
  onDiscard,
  onSuccess,
  onError,
  onBodySizeExceeded,
  attachments,
  onFilesSelected,
  isSendDisabled,
  isUploadingAttachments,
  isAttachmentUploadDisabled,
  outboundFields
}: EmailEditorProps): JSX.Element => {
  const { selectedEmail } = useEmailContext();
  const [activeListType, setActiveListType] = useState<'ordered' | 'unordered' | null>(null);
  const [format, setFormat] = useState<[boolean, boolean]>([false, false]);
  const [isBold, isItalic] = format;

  return (
    <Box position={'relative'}>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={
            <Box sx={{ margin: '14px 0', minHeight: '100px' }}>
              <ContentEditable className="contentEditable" />
            </Box>
          }
          placeholder={<Box className="placeholder">Write your reply...</Box>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <EmailEditorEventPlugin setFormat={setFormat} onChange={() => {}} setActiveListType={setActiveListType} />
        <AutoFocusPlugin />
        <HistoryPlugin />
        <ListPlugin />
        <AutoLinkPlugin matchers={MATCHERS} />
        <ClickableLinkPlugin />
        <LinkPlugin validateUrl={validateUrl} />
        <ImagePastePlugin onBodySizeExceeded={onBodySizeExceeded} />
        <TemplatePreview />
        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <QuickRepliesContainer
              channel="email"
              selectedContact={selectedEmail}
              QuickRepliesComponent={EmailQuickReplies}
            />
            <EmailMessageTemplates />
            <MarkdownButton formatType="bold" isActive={isBold} Icon={BoldIcon} />
            <MarkdownButton formatType="italic" isActive={isItalic} Icon={ItalicIcon} />
            <MarkdownListButton
              listType="unordered"
              activeListType={activeListType}
              setActiveListType={setActiveListType}
              Icon={BulletListIcon}
            />
            <MarkdownListButton
              listType="ordered"
              activeListType={activeListType}
              setActiveListType={setActiveListType}
              Icon={NumberedListIcon}
            />
            <LinkButton />
            <ImageUploadButton onBodySizeExceeded={onBodySizeExceeded} />
            <AttachmentUploadButton
              onFilesSelected={onFilesSelected}
              disabled={
                attachments.length >= MAX_ATTACHMENT_COUNT || isUploadingAttachments || isAttachmentUploadDisabled
              }
            />
            <Box sx={{ marginX: '8px', height: '20px' }}>
              <Divider orientation="vertical" />
            </Box>
            <DiscardButton onClick={onDiscard} />
          </Box>
          <SendEmailPlugin
            onDiscard={onDiscard}
            onSuccess={onSuccess}
            onError={onError}
            isSendDisabled={isSendDisabled}
            outboundFields={outboundFields}
            attachments={attachments}
          />
        </Box>
      </LexicalComposer>
    </Box>
  );
};

export default EmailEditor;
